import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { NumberRule } from '@/classes/rules/NumberRule';
import { DefaultValue } from './DefaultValue';
import { getValueFromPath } from '@/helpers/pathHelper';
import { normalizeFloat } from '@/helpers/mathHelper';

/**
 * Extension of NumberRule with min and max property, checking the value is between min and max.
 */
export class MinMaxRule<Context = ScenarioExt> extends NumberRule<Context> implements IRule<Context> {

  public max: number | string | undefined | ((context: Context) => number | undefined);
  public min: number | string | undefined | ((context: Context) => number | undefined);
  private precision = 3;

  public constructor(
    key: string,
    min?: number | string | ((context: Context) => number | undefined), // TODO use with setters instead of constructor arguments
    max?: number | string | ((context: Context) => number | undefined), // TODO use with setters instead of constructor arguments
    unitString?: string,
    isAvailable?: (context: Context) => boolean,
    factor?: number,
    getDefaultValue?: (context?: Context) => DefaultValue<number> | null,
    required?: boolean,
    isInteger?: boolean
  ) {
    super(key, unitString, isAvailable, factor, getDefaultValue, required, isInteger);
    this.min = min;
    this.max = max;
  }

  public checkValue(val: number | null, context: Context): boolean | null {
    if (!this.required && val === null) {
      return true;
    }
    if (val === null) {
      return null;
    }
    if (this.isInteger && !Number.isInteger(val)) {
      return false;
    }
    const minimum = this.readFromContext(this.min, context);
    if (typeof minimum === 'number' && val < minimum) {
      return false;
    }
    const maximum = this.readFromContext(this.max, context);
    if (typeof maximum === 'number' && val > maximum) {
      return false;
    }
    return true;
  }

  public vValidateRule(context: Context): string {
    let vValidateRule = '';
    vValidateRule += this.required ? 'required|' : '';
    vValidateRule += this.isInteger ? 'numeric|' : '';
    const minimumValue = this.getMinimum(context);
    const maximumValue = this.getMaximum(context);
    vValidateRule += maximumValue === undefined && minimumValue !== undefined ? `min_value: ${minimumValue}` : '';
    vValidateRule += minimumValue === undefined && maximumValue !== undefined ? `max_value: ${maximumValue}` : '';
    vValidateRule += minimumValue !== undefined && maximumValue !== undefined ? `between:${minimumValue},${maximumValue}` : '';
    return vValidateRule;
  }

  public getMinimum(context: Context): number | undefined {
    const minimum = this.readFromContext(this.min, context);
    return typeof minimum === 'number' ? normalizeFloat(minimum * this.factor, this.precision) : undefined;
  }

  public getMaximum(context: Context): number | undefined {
    const maximum = this.readFromContext(this.max, context);
    return typeof maximum === 'number' ? normalizeFloat(maximum * this.factor, this.precision) : undefined;
  }

  public withMinimum(minimum: number | string | ((context: Context) => number | undefined)): this {
    this.min = minimum;
    return this;
  }

  public withMaximum(maximum: number | string | ((context: Context) => number | undefined)): this {
    this.max = maximum;
    return this;
  }

  public withPrecision(precision: number): this {
    this.precision = precision;
    return this;
  }

  private readFromContext(value: string | number | undefined | ((context: Context) => number | undefined), context: Context): unknown {
    if (typeof value === 'function') {
      return value(context);
    }
    if (typeof value === 'number') {
      return value;
    }
    const [, ...pathToValue] = value?.split('.') ?? [];
    return getValueFromPath(context, pathToValue);
  }
}
